import { useTranslation } from "react-i18next";
import clsx from "clsx";
import PropTypes from "prop-types";
import styles from "./TextArea.module.scss";

const countWords = (text) =>
  text.trim() === "" ? 0 : text.trim().split(/\s+/).length;

const TextArea = ({
  variant = "default",
  value,
  className = "",
  fieldClassName = "",
  textareaClassName = "",
  counterInside = false,
  placeholder = "",
  onChange = () => {},
  maxLength,
  maxWords,
  warningText,
  allowFooterMarginTop = true,
  ...props
}) => {
  const onValueChange = (e) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) {
      return;
    }
    if (maxWords && countWords(newValue) > maxWords) {
      return;
    }
    onChange(newValue);
  };

  const { t } = useTranslation();
  const normalizedValue = value ?? "";
  const normalizedValueLength =
    typeof normalizedValue === "string"
      ? normalizedValue.length
      : String(normalizedValue).length;

  const wordCount = maxWords ? countWords(String(normalizedValue)) : 0;
  const isAtLimit =
    (!!maxLength && normalizedValueLength >= maxLength) ||
    (!!maxWords && wordCount >= maxWords);

  const showFooter = (!!maxLength || !!maxWords) && variant !== "note";
  const counterLabel = maxWords
    ? `${wordCount}/${maxWords}`
    : `${normalizedValueLength}/${maxLength}`;

  return (
    <div className={clsx(className)}>
      <div
        className={clsx(
          "relative h-full rounded border border-transparent",
          fieldClassName,
        )}
      >
        <textarea
          value={normalizedValue}
          onChange={onValueChange}
          className={clsx(
            styles.textarea,
            styles[variant],
            counterInside && showFooter && "pb-12 pr-24",
            textareaClassName,
          )}
          placeholder={t(placeholder)}
          maxLength={maxLength || undefined}
          {...props}
          style={{ paddingLeft: "1rem" }}
        />
        {showFooter && counterInside && (
          <span className="absolute bottom-3 right-4 z-10 rounded bg-white/95 px-1 text-gray-extra-light text-xs leading-4 pointer-events-none">
            {counterLabel}
          </span>
        )}
      </div>
      {showFooter && !counterInside && (
        <div
          className={clsx(
            "flex items-center justify-between px-1",
            allowFooterMarginTop && "mt-3",
          )}
        >
          {isAtLimit && warningText ? (
            <span className="text-gray-extra-light">{warningText}</span>
          ) : (
            <span />
          )}
          <span className="text-gray-extra-light ml-auto">
            {counterLabel}
          </span>
        </div>
      )}
    </div>
  );
};

TextArea.propTypes = {
  variant: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  fieldClassName: PropTypes.string,
  textareaClassName: PropTypes.string,
  counterInside: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default TextArea;
